"use client";

import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import Link from 'next/link';
import { useFormState } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useFormStatus } from 'react-dom';
import { loginAction, registerAction } from '../actions';
import { PHONE_COUNTRY_CODES } from '@/lib/phone';
import { GoogleLoginButton } from './google-login-button';

interface AuthFormProps {
  mode: 'login' | 'register';
  redirectTo?: string;
}

interface AuthFormState {
  error?: string;
}

export function AuthForm({ mode, redirectTo }: AuthFormProps) {
  const isRegister = mode === 'register';
  const action = isRegister ? registerAction : loginAction;
  const [showPassword, setShowPassword] = useState(false);
  const [state, formAction] = useFormState<AuthFormState, FormData>(
    action,
    {}
  );

  return (
    <form action={formAction} className="space-y-5 w-full">
      {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
      {isRegister && (
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium">
            Full name
          </label>
          <input
            id="name"
            name="name"
            required
            autoComplete="name"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/80"
          />
        </div>
      )}

      <div>
        <label htmlFor="email" className="mb-1 block text-sm font-medium">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/80"
        />
      </div>

      {isRegister && (
        <div>
          <label htmlFor="phoneCountryCode" className="mb-1 block text-sm font-medium">
            Country code
          </label>
          <input
            id="phoneCountryCode"
            name="phoneCountryCode"
            type="text"
            list="phone-country-codes"
            defaultValue="+506"
            required
            className="mb-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/80"
            placeholder="+506"
          />
          <datalist id="phone-country-codes">
            {PHONE_COUNTRY_CODES.map((code) => (
              <option key={code} value={code} />
            ))}
          </datalist>

          <label htmlFor="phone" className="mb-1 block text-sm font-medium">
            Phone
          </label>
          <input
            id="phone"
            name="phone"
            autoComplete="tel"
            placeholder="00000000"
            required
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/80"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Enter only the local number. Country code is selected above (for example +1 + 00000000).
          </p>
        </div>
      )}

      <div>
        <label htmlFor="password" className="mb-1 block text-sm font-medium">
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            required
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            className="w-full rounded-md border border-border bg-background px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring/80"
          />
          <button
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            onClick={() => setShowPassword((previous) => !previous)}
            className="absolute right-0 top-0 flex h-full items-center pr-3 text-muted-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" name="rememberMe" />
        Remember me
      </label>

      {state?.error ? (
        <p className="text-sm text-destructive">{state.error}</p>
      ) : null}

      <AuthSubmitButton isRegister={isRegister} />

      {!isRegister && (
        <div className="text-center">
          <Link
            href="/auth/forgot-password"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
          >
            Forgot your password?
          </Link>
        </div>
      )}

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <GoogleLoginButton redirectTo={redirectTo} />

      <p className="text-sm text-muted-foreground text-center mt-6">
        {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
        <Link
          href={isRegister ? '/auth/login' : '/auth/register'}
          className="text-foreground font-bold hover:text-primary transition-colors underline-offset-4 hover:underline"
        >
          {isRegister ? 'Sign in' : 'Register Now'}
        </Link>
      </p>
    </form>
  );
}

function AuthSubmitButton({ isRegister }: { isRegister: boolean }) {
  const { pending } = useFormStatus();

  return (
    <Button type="submit" className="w-full" disabled={pending}>
      {pending ? 'Please wait...' : isRegister ? 'Create account' : 'Sign in'}
    </Button>
  );
}
